import { useEffect, useMemo, useRef } from "react";
import { useDispatch } from "../redux/store";
import ReactGA4 from "react-ga4";
import tinycolor from "tinycolor2";

import type { AppointmentState } from "../types";
import { updateAppointment } from "../redux/slices/appointmentSlice";
import { sendToWindowDataLayer } from "../utils";
import { postBookingConfig } from "../utils/api";
import { getAppointmentTheme } from "../utils/theme";
import { BookingStage } from "../constants/bookingStages";

interface AppointmentSubmittingProps {
  appointment: AppointmentState;
}

interface BookingResult {
  confirmed?: boolean;
  message?: string;
}

const AppointmentSubmitting = ({ appointment }: AppointmentSubmittingProps) => {
  const dispatch = useDispatch();
  const hasSubmitted = useRef(false);

  const { accentColor, gradientBackground } = useMemo(
    () => getAppointmentTheme(appointment),
    [appointment]
  );

  useEffect(() => {
    // Prevent duplicate submissions
    if (hasSubmitted.current) {
      return;
    }
    hasSubmitted.current = true;

    let mounted = true;

    const submitAppointment = async (): Promise<void> => {
      try {
        const bookingResult = (await postBookingConfig(
          appointment
        )) as BookingResult;
        const confirmed = Boolean(bookingResult?.confirmed);

        if (!mounted) return;

        if (confirmed) {
          sendToWindowDataLayer("dc-service-booked", "dc-service-booked");
          (ReactGA4.event as (payload: Record<string, unknown>) => void)({
            category: "Appointments",
            action: "scheduled",
            label: "APPOINTMENT SCHEDULED",
            utm_data: appointment?.utmData,
          });

          dispatch(
            updateAppointment({
              updates: {
                stage: BookingStage.Confirmed,
                bookingError: undefined,
              },
            })
          );
          return;
        }

        dispatch(
          updateAppointment({
            updates: {
              stage: BookingStage.BookingFailed,
              bookingError:
                bookingResult?.message ||
                "We couldn't confirm your appointment with the shop system.",
            },
          })
        );
      } catch (error: unknown) {
        if (!mounted) return;

        const responseMessage =
          typeof error === "object" &&
          error !== null &&
          "response" in error &&
          typeof (error as { response?: { message?: unknown } }).response ===
            "object"
            ? (error as { response?: { message?: unknown } }).response?.message
            : undefined;
        const errorMessage =
          typeof error === "object" &&
          error !== null &&
          "message" in error &&
          typeof (error as { message?: unknown }).message === "string"
            ? (error as { message: string }).message
            : undefined;

        dispatch(
          updateAppointment({
            updates: {
              stage: BookingStage.BookingFailed,
              bookingError:
                (typeof responseMessage === "string" && responseMessage) ||
                errorMessage ||
                "There was a problem creating your appointment.",
            },
          })
        );
      }
    };

    submitAppointment();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  return (
    <div
      className="flex flex-col items-center justify-center min-h-full text-center font-roboto"
      style={{ background: gradientBackground }}
    >
      <div className="w-full max-w-md px-4 py-8 md:px-6 box-border">
        <div
          className="rounded-2xl bg-white px-6 py-8"
          style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
        >
          <div
            className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full"
            style={{
              backgroundColor: tinycolor(accentColor).lighten(35).toHexString(),
            }}
          >
            <i
              className="fa-solid fa-tire-rugged animate-spin text-3xl"
              style={{ color: accentColor, lineHeight: 1 }}
              aria-hidden="true"
            />
          </div>

          <div className="mb-2 text-2xl font-bold text-gray-900">
            Creating Your Appointment
          </div>
          <div className="text-sm leading-relaxed text-gray-600">
            Please wait while we confirm your appointment with the shop&apos;s
            system.
          </div>
        </div>
      </div>
    </div>
  );
};

export { AppointmentSubmitting };
